import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Receipt,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCcw,
  ShieldCheck,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useOrders } from '@/hooks/useBilling';
import { formatDate, formatUsd } from '@/lib/utils';
import { cn } from '@/lib/utils';

type OrderRich = {
  id: string;
  package_id: string;
  package_name: string | null;
  provider: string | null;
  payment_method: string | null;
  amount_paid: number | null;
  currency: string | null;
  status: 'pending' | 'pending_verification' | 'paid' | 'failed' | 'refunded';
  credits_granted: number | null;
  paid_at: number | null;
  created_at: number;
  cart_id: string | null;
  crypto_asset?: string | null;
  crypto_network?: string | null;
  crypto_expected_amount?: number | null;
  crypto_txid?: string | null;
  crypto_txid_submitted_at?: number | null;
  verification_notes?: string | null;
};

// A group is either a single legacy order (cart_id null) or a set of
// orders sharing the same cart_id. The sample order carries display
// metadata (package name, crypto details) that's identical within the
// group for same-package buys.
type OrderGroup = {
  key: string;
  cart_id: string | null;
  sample: OrderRich;
  items: OrderRich[];
  distinct_packages: number;
  total_amount: number | null;
  total_credits: number | null;
  status: OrderRich['status'] | 'mixed';
};

function groupOrders(orders: OrderRich[]): OrderGroup[] {
  const groups = new Map<string, OrderRich[]>();
  for (const o of orders) {
    const k = o.cart_id ?? `solo:${o.id}`;
    const arr = groups.get(k) ?? [];
    arr.push(o);
    groups.set(k, arr);
  }
  return Array.from(groups.entries()).map(([key, items]) => {
    const sample = items[0];
    const pkgIds = new Set(items.map((i) => i.package_id));
    const statuses = new Set(items.map((i) => i.status));
    const totalAmount = items.reduce<number | null>(
      (s, i) => (i.amount_paid == null ? s : (s ?? 0) + i.amount_paid),
      null,
    );
    const totalCredits = items.reduce<number | null>(
      (s, i) => (i.credits_granted == null ? s : (s ?? 0) + i.credits_granted),
      null,
    );
    return {
      key,
      cart_id: sample.cart_id,
      sample,
      items,
      distinct_packages: pkgIds.size,
      total_amount: totalAmount,
      total_credits: totalCredits,
      status: statuses.size === 1 ? sample.status : 'mixed',
    };
  });
}

function formatAmount(amount: number | null, currency: string | null): string {
  if (amount == null || !currency) return '—';
  const value = amount / 100;
  const locale = currency === 'try' ? 'tr-TR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(value);
}

export function OrdersPage() {
  const { data, isPending } = useOrders();
  const orders = (data?.orders ?? []) as unknown as OrderRich[];
  const groups = useMemo(() => groupOrders(orders), [orders]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Receipt className="w-5 h-5" /> Satın Alma Geçmişi
        </h1>
        <p className="text-muted-foreground text-sm">Tüm ödeme ve bakiye yükleme kayıtların.</p>
      </div>

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Henüz satın alma kaydın yok.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-2 px-3 font-medium">Tarih</th>
                  <th className="text-left py-2 px-3 font-medium">Paket</th>
                  <th className="text-left py-2 px-3 font-medium">Yöntem</th>
                  <th className="text-left py-2 px-3 font-medium">Tutar</th>
                  <th className="text-right py-2 px-3 font-medium">Kredi</th>
                  <th className="text-center py-2 px-3 font-medium">Durum</th>
                  <th className="text-right py-2 px-3 font-medium">Aksiyon</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <OrderRow key={g.key} group={g} />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function OrderRow({ group }: { group: OrderGroup }) {
  const { sample, items, distinct_packages, total_amount, total_credits, status, cart_id } = group;
  const qty = items.length;
  const isCryptoCart = sample.payment_method === 'crypto' && cart_id != null;
  // Legacy single-item crypto orders have no cart_id and their checkout
  // flow is gone — show them as history-only rows (no action button).
  const showAction =
    sample.payment_method === 'crypto' &&
    cart_id != null &&
    (status === 'pending' || status === 'pending_verification');

  // Name: same pkg x qty | mixed: "Karma sepet (N kalem)" | single: pkg name
  const nameLabel =
    distinct_packages === 1
      ? qty > 1
        ? `${sample.package_name ?? sample.package_id} × ${qty}`
        : sample.package_name ?? sample.package_id
      : `Karma Sepet (${qty} kalem)`;

  // For grouped carts, the "order id" below the date is confusing
  // (it's one of N). Show cart id instead, which actually identifies
  // the payment.
  const refId = cart_id ?? sample.id;

  return (
    <tr className="border-b last:border-b-0">
      <td className="py-2 px-3">
        <div>{formatDate(sample.paid_at ?? sample.created_at)}</div>
        <div className="text-xs text-muted-foreground font-mono">{refId}</div>
      </td>
      <td className="py-2 px-3">{nameLabel}</td>
      <td className="py-2 px-3">
        {sample.payment_method === 'crypto' ? (
          <span className="inline-flex items-center gap-1 text-xs">
            🪙 {sample.crypto_asset} ({sample.crypto_network})
          </span>
        ) : (
          <span className="text-xs">Kredi Kartı</span>
        )}
      </td>
      <td className="py-2 px-3 font-mono">
        {isCryptoCart && sample.crypto_expected_amount
          ? `${sample.crypto_expected_amount} ${sample.crypto_asset}`
          : sample.payment_method === 'crypto' && sample.crypto_expected_amount && qty === 1
          ? `${sample.crypto_expected_amount} ${sample.crypto_asset}`
          : formatAmount(total_amount, sample.currency)}
      </td>
      <td className="py-2 px-3 text-right font-mono">
        {total_credits != null && total_credits > 0 ? `+${formatUsd(total_credits, 2)}` : '—'}
      </td>
      <td className="py-2 px-3 text-center">
        <StatusBadge status={status} />
      </td>
      <td className="py-2 px-3 text-right">
        {showAction && cart_id && (
          <Button size="sm" variant="outline" asChild>
            <Link to="/checkout/cart/$cartId" params={{ cartId: cart_id }}>
              {status === 'pending' ? 'TXID Gönder' : 'Durumu Gör'}
              <ArrowRight className="w-3 h-3" />
            </Link>
          </Button>
        )}
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: OrderRich['status'] | 'mixed' }) {
  const map: Record<
    OrderRich['status'] | 'mixed',
    { label: string; cls: string; icon: React.ComponentType<{ className?: string }> }
  > = {
    pending: { label: 'Ödeme bekliyor', cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', icon: Clock },
    pending_verification: {
      label: 'Doğrulanıyor',
      cls: 'bg-primary/10 text-primary',
      icon: ShieldCheck,
    },
    paid: {
      label: 'Ödendi',
      cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
      icon: CheckCircle2,
    },
    failed: { label: 'Başarısız', cls: 'bg-destructive/10 text-destructive', icon: XCircle },
    refunded: { label: 'İade', cls: 'bg-muted text-muted-foreground', icon: RotateCcw },
    mixed: { label: 'Karma', cls: 'bg-muted text-muted-foreground', icon: Clock },
  };
  const m = map[status] ?? map.pending;
  const Icon = m.icon;
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', m.cls)}
    >
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}
